(() => {
  const DEPOSIT_VARIANT_ID = 55334564692302;
  const DEPOSIT_HANDLE = "einwegpfand-1";
  const PACKAGING_VARIANT_IDS = new Set([55150991409486]);
  const DISPLAY_PACKAGING_VARIANT_IDS = new Set([55150991409486, 60685224673614]);
  const PACKAGING_HANDLES = new Set(["3er-box"]);
  const METADATA_SECTION = "deposit-cart-metadata";
  const METADATA_URL = `/cart?sections=${METADATA_SECTION}`;
  const SYNC_DELAYS = [250, 1200, 2600];
  const REFRESH_SECTIONS = ["cart-drawer", "cart-icon-bubble", "main-cart-items", "main-cart-footer"];
  const RELOAD_GUARD_PREFIX = "wntnDepositReload";

  let updateInFlight = null;
  let scheduledTimers = [];
  let refreshInFlight = null;
  let displayedCartCount = null;
  let displayedBottleCount = null;

  function toPositiveInteger(value) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  function isDepositLine(item) {
    return item?.variant_id === DEPOSIT_VARIANT_ID || item?.handle === DEPOSIT_HANDLE;
  }

  function getCustomerFacingCartCount(cart) {
    return cart.items.reduce((sum, item) => {
      const boxRole = normalizeText(item?.properties?._wntn_box_role);
      const boxSize = toPositiveInteger(item?.properties?._wntn_box_size);
      const packType = normalizeText(item?.properties?._wntn_pack_type);
      const quantityStep = toPositiveInteger(item?.properties?._wntn_quantity_step);
      if (
        isDepositLine(item) ||
        DISPLAY_PACKAGING_VARIANT_IDS.has(item?.variant_id) ||
        boxRole === "box" ||
        boxRole === "byob_box"
      ) {
        return sum;
      }

      const quantity = toPositiveInteger(item?.quantity);
      if (boxRole === "wine" && boxSize > 1) return sum + 1;
      if (packType === "kiste" && quantityStep > 1) return sum + 1;
      return sum + quantity;
    }, 0);
  }

  function getCustomerFacingBottleCount(cart) {
    const depositLine = cart.items.find(isDepositLine);
    return toPositiveInteger(depositLine?.quantity);
  }

  function getCustomerFacingCartSummary(cart) {
    const cartCount = getCustomerFacingCartCount(cart);
    const bottleCount = getCustomerFacingBottleCount(cart);
    const productLabel = `${cartCount} ${cartCount === 1 ? "Produkt" : "Produkte"}`;

    if (bottleCount === 0) return productLabel;
    return `${productLabel} · ${bottleCount} ${bottleCount === 1 ? "Flasche" : "Flaschen"}`;
  }

  function applyCustomerFacingCartCount(cart) {
    const cartCount = getCustomerFacingCartCount(cart);
    const bottleCount = getCustomerFacingBottleCount(cart);
    displayedCartCount = cartCount;
    displayedBottleCount = bottleCount;

    const countBubble = document.querySelector("#cart-icon-bubble .cart-count-bubble");
    const visibleCount = countBubble?.querySelector('[aria-hidden="true"]');
    if (visibleCount && visibleCount.textContent !== `${cartCount}`) {
      visibleCount.textContent = cartCount;
    }

    const accessibleCount = countBubble?.querySelector(".visually-hidden");
    if (accessibleCount) {
      const updatedText = accessibleCount.textContent.replace(/^\s*\d+/, cartCount);
      if (accessibleCount.textContent !== updatedText) accessibleCount.textContent = updatedText;
    }

    const drawerBottleCount = document.querySelector(".drawer__bottle-count");
    if (drawerBottleCount) {
      const label = getCustomerFacingCartSummary(cart);
      if (drawerBottleCount.textContent.trim() !== label) drawerBottleCount.textContent = label;
    }
  }

  function isPackagingLine(item, metadata = {}) {
    return (
      PACKAGING_VARIANT_IDS.has(item?.variant_id) ||
      PACKAGING_HANDLES.has(item?.handle) ||
      PACKAGING_HANDLES.has(metadata.handle)
    );
  }

  function normalizeText(value) {
    return `${value || ""}`.toLowerCase();
  }

  function isByobLine(item, metadata = {}) {
    const haystack = [
      item?.product_title,
      item?.title,
      item?.handle,
      metadata.title,
      metadata.handle,
      item?.properties?.["enthaltene Produkte"],
    ]
      .map(normalizeText)
      .join(" ");

    return (
      Boolean(item?.properties?.__byob) ||
      Boolean(metadata.isByob) ||
      haystack.includes("deine eigene") ||
      haystack.includes("erbox-erstellen")
    );
  }

  function parseByobUnits(item) {
    const rawByob = item?.properties?.__byob;
    if (!rawByob) return 0;

    try {
      const byob = typeof rawByob === "string" ? JSON.parse(rawByob) : rawByob;
      if (!Array.isArray(byob?.c)) return 0;

      return byob.c.reduce((sum, component) => {
        if (!Array.isArray(component)) return sum;
        return sum + toPositiveInteger(component[1]);
      }, 0);
    } catch (_error) {
      return 0;
    }
  }

  function parseTitleFallbackUnits(item, metadata) {
    const title = [
      item?.product_title,
      item?.title,
      metadata?.title,
      item?.properties?.["enthaltene Produkte"],
      item?.properties?.["Included Products"],
    ].join(" ");
    const match = title.match(/(\d+)\s*er\s*box/i);
    return match ? toPositiveInteger(match[1]) : 0;
  }

  function parseIncludedProductsUnits(item) {
    if (!isByobLine(item)) return 0;

    const includedProducts = item?.properties?.["enthaltene Produkte"] || item?.properties?.["Included Products"];
    if (!includedProducts) return 0;

    return `${includedProducts}`
      .split(",")
      .reduce((sum, productName) => {
        const quantity = productName.match(/\bx\s*(\d+)\b/i);
        return sum + (quantity ? toPositiveInteger(quantity[1]) : 1);
      }, 0);
  }

  async function fetchDepositMetadata() {
    try {
      const response = await fetch(`${METADATA_URL}&_=${Date.now()}`, { credentials: "same-origin" });
      const payload = await response.json();
      const html = payload[METADATA_SECTION];
      if (!html) return {};

      const doc = new DOMParser().parseFromString(html, "text/html");
      const script = doc.querySelector("#deposit-cart-metadata-json");
      const metadata = JSON.parse(script?.textContent || '{"lines":[]}');

      return (metadata.lines || []).reduce((byKey, line) => {
        byKey[line.key] = {
          depositUnits: toPositiveInteger(line.deposit_units),
          handle: line.handle || "",
          isByob: Boolean(line.is_byob),
          title: line.title || "",
          variantId: line.variant_id,
        };
        return byKey;
      }, {});
    } catch (_error) {
      return {};
    }
  }

  function getLineDepositUnits(item, metadataByKey) {
    if (!item || isDepositLine(item)) return 0;

    const quantity = toPositiveInteger(item.quantity);
    if (quantity === 0) return 0;

    const metadata = metadataByKey[item.key] || {};
    if (isPackagingLine(item, metadata)) return 0;

    const byobUnits = parseByobUnits(item);
    const propertyUnits = toPositiveInteger(item.properties?._deposit_units);
    const metadataUnits = toPositiveInteger(metadata.depositUnits);
    const includedProductsUnits = parseIncludedProductsUnits(item);
    const titleFallbackUnits = parseTitleFallbackUnits(item, metadata);

    const unitsPerLine = byobUnits || propertyUnits || metadataUnits || includedProductsUnits || titleFallbackUnits;
    return unitsPerLine * quantity;
  }

  async function setDepositQuantity(cart, bottles) {
    const depositLine = cart.items.find(isDepositLine);

    if (bottles === 0 && depositLine) {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ id: depositLine.key, quantity: 0 }),
      });
      return true;
    }

    if (bottles > 0 && !depositLine) {
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          id: DEPOSIT_VARIANT_ID,
          quantity: bottles,
          properties: { _pfand_auto: "1" },
        }),
      });
      return true;
    }

    if (depositLine && depositLine.quantity !== bottles) {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ id: depositLine.key, quantity: bottles }),
      });
      return true;
    }

    return false;
  }

  async function updateDeposit() {
    if (updateInFlight) return updateInFlight;

    updateInFlight = (async () => {
      const [cart, metadataByKey] = await Promise.all([
        fetch("/cart.js", { credentials: "same-origin" }).then((response) => response.json()),
        fetchDepositMetadata(),
      ]);
      const bottles = cart.items.reduce((sum, item) => sum + getLineDepositUnits(item, metadataByKey), 0);

      return setDepositQuantity(cart, bottles);
    })();

    try {
      return await updateInFlight;
    } finally {
      updateInFlight = null;
    }
  }

  window.updateDeposit = updateDeposit;

  async function fetchCart() {
    return fetch("/cart.js", { credentials: "same-origin" }).then((response) => response.json());
  }

  function replaceSection(parsedSections, sectionId, selector) {
    const sectionHtml = parsedSections[sectionId];
    if (!sectionHtml) return;

    const doc = new DOMParser().parseFromString(sectionHtml, "text/html");
    const source = doc.querySelector(selector);
    const target = document.querySelector(selector);
    if (source && target) target.replaceWith(source);
  }

  function replaceSectionContents(parsedSections, sectionId, sourceSelector, targetSelector) {
    const sectionHtml = parsedSections[sectionId];
    if (!sectionHtml) return;

    const doc = new DOMParser().parseFromString(sectionHtml, "text/html");
    const source = doc.querySelector(sourceSelector);
    const target = document.querySelector(targetSelector);
    if (source && target) target.innerHTML = source.innerHTML;
  }

  async function refreshCartSurfaces() {
    if (refreshInFlight) return refreshInFlight;

    refreshInFlight = (async () => {
      const response = await fetch(`/cart?sections=${REFRESH_SECTIONS.join(",")}`, { credentials: "same-origin" });
      const sections = await response.json();

      replaceSection(sections, "cart-drawer", "cart-drawer-items");
      replaceSection(sections, "cart-drawer", ".cart-drawer__footer");
      replaceSection(sections, "cart-drawer", ".drawer__heading-group");
      replaceSectionContents(
        sections,
        "cart-icon-bubble",
        "#shopify-section-cart-icon-bubble",
        "#cart-icon-bubble"
      );
      replaceSection(sections, "main-cart-items", "#main-cart-items .js-contents");
      replaceSection(sections, "main-cart-footer", "#main-cart-footer .js-contents");

      document.querySelector("cart-drawer")?.setHeaderCartIconAccessibility?.();
    })();

    try {
      return await refreshInFlight;
    } catch (_error) {
      return undefined;
    } finally {
      refreshInFlight = null;
    }
  }

  function isMainCartPage() {
    return (
      window.location.pathname.replace(/\/$/, "").endsWith("/cart") ||
      Boolean(document.querySelector("#main-cart-items, #main-cart-footer, cart-items"))
    );
  }

  function getVisibleDepositAmountInCents() {
    const depositElement = document.querySelector("[data-deposit-total-cents]");
    if (depositElement) return toPositiveInteger(depositElement.dataset.depositTotalCents);

    const depositText = Array.from(document.querySelectorAll(".totals"))
      .map((element) => element.textContent || "")
      .find((text) => /pfand/i.test(text));
    if (!depositText) return 0;

    const match = depositText.match(/€\s*([\d.,]+)|([\d.,]+)\s*€/);
    if (!match) return 0;

    const amount = (match[1] || match[2] || "0").replace(/\./g, "").replace(",", ".");
    return Math.round(parseFloat(amount) * 100) || 0;
  }

  function reloadGuardKey(cart, cartDepositAmount) {
    const nonDepositItems = cart.items
      .filter((item) => !isDepositLine(item))
      .map((item) => `${item.key}:${item.quantity}`)
      .join("|");
    return `${RELOAD_GUARD_PREFIX}:${cartDepositAmount}:${nonDepositItems}`;
  }

  function canReloadForCartState(cart, cartDepositAmount) {
    try {
      const key = reloadGuardKey(cart, cartDepositAmount);
      if (window.sessionStorage.getItem(key) === "1") return false;

      window.sessionStorage.setItem(key, "1");
      return true;
    } catch (_error) {
      return true;
    }
  }

  async function mainCartDisplayNeedsReload() {
    if (!isMainCartPage()) return false;

    try {
      const cart = await fetchCart();
      const depositLine = cart.items.find(isDepositLine);
      const cartDepositAmount = depositLine ? depositLine.final_line_price : 0;
      return cartDepositAmount !== getVisibleDepositAmountInCents() && canReloadForCartState(cart, cartDepositAmount);
    } catch (_error) {
      return false;
    }
  }

  async function canReloadAfterDepositChange() {
    if (!isMainCartPage()) return false;

    try {
      const cart = await fetchCart();
      const depositLine = cart.items.find(isDepositLine);
      const cartDepositAmount = depositLine ? depositLine.final_line_price : 0;
      return canReloadForCartState(cart, cartDepositAmount);
    } catch (_error) {
      return true;
    }
  }

  async function syncDepositAndRefresh() {
    const changed = await updateDeposit();
    const shouldReloadMainCart = changed ? await canReloadAfterDepositChange() : await mainCartDisplayNeedsReload();

    if (shouldReloadMainCart && isMainCartPage()) {
      window.setTimeout(() => window.location.reload(), 250);
      return changed;
    }

    if (changed || isMainCartPage() || document.querySelector("cart-drawer.active")) {
      await refreshCartSurfaces();
    }

    applyCustomerFacingCartCount(await fetchCart());

    return changed;
  }

  function scheduleDepositSync(delays = SYNC_DELAYS) {
    const existingDelays = scheduledTimers.map((entry) => entry.delay);
    const nextDelays = delays.filter((delay) => !existingDelays.includes(delay));

    scheduledTimers = scheduledTimers.concat(nextDelays.map((delay) => {
      const timer = window.setTimeout(() => {
        scheduledTimers = scheduledTimers.filter((entry) => entry.timer !== timer);
        syncDepositAndRefresh();
      }, delay);
      return { delay, timer };
    }));
  }

  function isAddToCartTrigger(element) {
    if (!element) return false;

    const label = `${element.textContent || ""} ${element.getAttribute?.("aria-label") || ""}`.toLowerCase();
    return (
      element.matches?.('[name="add"], [data-add-to-cart], [type="submit"]') ||
      label.includes("warenkorb") ||
      label.includes("cart")
    );
  }

  function isCartQuantityTrigger(element) {
    if (!element) return false;

    return Boolean(
      element.closest?.("cart-remove-button") ||
        element.closest?.("quantity-input") ||
        element.matches?.(".quantity__button, .quantity__input, [name='updates[]']")
    );
  }

  document.addEventListener("cart:updated", () => {
    scheduleDepositSync();
  });

  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      scheduleDepositSync();
    });
  }

  document.addEventListener(
    "click",
    (event) => {
      const control = event.target.closest("button, a, input, cart-remove-button, quantity-input");
      if (isAddToCartTrigger(control)) {
        scheduleDepositSync([900, 2200, 4200]);
      } else if (isCartQuantityTrigger(control)) {
        scheduleDepositSync([900, 2200, 4200]);
      }
    },
    true
  );

  document.addEventListener(
    "change",
    (event) => {
      if (isCartQuantityTrigger(event.target)) {
        scheduleDepositSync([900, 2200, 4200]);
      }
    },
    true
  );

  document.addEventListener(
    "submit",
    (event) => {
      const action = `${event.target?.getAttribute?.("action") || ""}`;
      if (action.includes("/cart/add") || action.includes("/cart")) {
        scheduleDepositSync([900, 2200, 4200]);
      }
    },
    true
  );

  document.addEventListener("DOMContentLoaded", () => {
    scheduleDepositSync([0, 1200]);

    const cartCountObserver = new MutationObserver(() => {
      if (displayedCartCount === null) return;

      const visibleCount = document.querySelector('#cart-icon-bubble .cart-count-bubble [aria-hidden="true"]');
      const drawerBottleCount = document.querySelector(".drawer__bottle-count");
      const expectedDrawerText = `${displayedCartCount} ${
        displayedCartCount === 1 ? "Produkt" : "Produkte"
      }${
        displayedBottleCount > 0
          ? ` · ${displayedBottleCount} ${displayedBottleCount === 1 ? "Flasche" : "Flaschen"}`
          : ""
      }`;

      if (
        (visibleCount && visibleCount.textContent !== `${displayedCartCount}`) ||
        (drawerBottleCount && drawerBottleCount.textContent.trim() !== expectedDrawerText)
      ) {
        fetchCart().then(applyCustomerFacingCartCount).catch(() => undefined);
      }
    });

    cartCountObserver.observe(document.body, { childList: true, subtree: true });
  });
})();
